import i18n from "i18next";

export const apiWeightToTonnes = (apiWeight) => {
  if (apiWeight == null || apiWeight === "" || apiWeight === undefined) {
    return null;
  }
  const w = Number(apiWeight);
  if (Number.isNaN(w)) return null;
  if (w >= 1) return w / 1000;
  if (w > 0) return w;
  return 0;
};

/**
 * @param {Object} period - { ongoingCharacterization, client, weight, waste_category, pendingCorrections?, status? }
 * @returns {string} ImageEnTraitement | ACompleter | CorrectionEnTraitement | Validated
 */
export const getCharacterizationStatus = (period) => {
  if (!period) return "ACompleter";

  if (period.ongoingCharacterization === true) {
    return "ImageEnTraitement";
  }

  const hasOrigin =
    period.client != null && String(period.client).trim() !== "";
  const weightNum = Number(period.weight);
  const hasWeight =
    period.weight != null &&
    String(period.weight).trim() !== "" &&
    !Number.isNaN(weightNum) &&
    weightNum > 0;
  let hasDenomination = false;
  if (
    period.waste_category != null &&
    String(period.waste_category).trim() !== ""
  ) {
    try {
      const parsed = JSON.parse(period.waste_category);
      hasDenomination = !!(parsed?.wasteCategoryName ?? parsed?.label);
    } catch {
      hasDenomination = false;
    }
  }

  if (!hasOrigin || !hasWeight || !hasDenomination) {
    return "ACompleter";
  }

  if (period.pendingCorrections === true) {
    return "CorrectionEnTraitement";
  }

  return "Validated";
};

/** Met à jour les champs de period utilisés par getCharacterizationStatus après sauvegarde client. */
export const buildPeriodUpdatesFromClientForm = (formValues) => {
  const clientData = {
    name: formValues.clientName || "",
    code: formValues.clientCode || "",
    address: formValues.clientAddress || "",
  };

  const weightTonnes = parseFloat(
    String(formValues.weight ?? "").replace(",", "."),
  );
  const weightInKg =
    !Number.isNaN(weightTonnes) && weightTonnes > 0
      ? weightTonnes * 1000
      : null;

  let waste_category = formValues.wasteCategoryName ?? "";
  if (waste_category && String(waste_category).trim() !== "") {
    try {
      const parsed = JSON.parse(waste_category);
      if (!(parsed?.wasteCategoryName ?? parsed?.label)) {
        waste_category = JSON.stringify({
          wasteCategoryName: formValues.wasteCategoryName,
        });
      }
    } catch {
      waste_category = JSON.stringify({
        wasteCategoryName: formValues.wasteCategoryName,
      });
    }
  }

  return {
    client: JSON.stringify(clientData),
    weight: weightInKg,
    waste_category,
  };
};

const PARENT_CATEGORY_ORDER_GOVERNMENT_REPORT = [
  "cardboard",
  "metal",
  "plastic",
  "glass",
  "wood",
  "mineral",
  "plaster",
  "biowaste",
  "textile",
  "other",
];

export const sortParentCategoriesListForReport = (list) => {
  if (!Array.isArray(list) || list.length === 0) return list;
  const order = PARENT_CATEGORY_ORDER_GOVERNMENT_REPORT;
  return [...list].sort((a, b) => {
    const keyA = String(a.parentCategory || "").toLowerCase();
    const keyB = String(b.parentCategory || "").toLowerCase();
    const indexA = order.indexOf(keyA);
    const indexB = order.indexOf(keyB);
    if (indexA === -1 && indexB === -1) return keyA.localeCompare(keyB);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

/** Clé de traduction réservée aux catégories (table Type de déchets), pas aux matières (graphique). */
const PARENT_CATEGORY_LABEL_KEY = "parent_category_cardboard";

export const getParentCategoryLabelPlural = (t, parentCategoryKey) => {
  if (!parentCategoryKey) return "";
  const keyLower = String(parentCategoryKey).toLowerCase();
  if (keyLower === "cardboard") {
    const categoryLabel = t(PARENT_CATEGORY_LABEL_KEY);
    return categoryLabel !== PARENT_CATEGORY_LABEL_KEY
      ? categoryLabel
      : t(parentCategoryKey);
  }
  const pluralKey = `${keyLower}_plural`;
  const plural = t(pluralKey);
  return plural !== pluralKey ? plural : t(parentCategoryKey);
};

export const sortByImageUrl = (a, b) => {
  const regex = /-(L|R)\./;
  const suffixA = a.image_url?.match(regex)?.[1];
  const suffixB = b.image_url?.match(regex)?.[1];

  if (suffixA === "L" && suffixB !== "L") {
    return -1;
  } else if (suffixA !== "L" && suffixB === "L") {
    return 1;
  } else if (suffixA === "R" && suffixB !== "R") {
    return -1;
  } else if (suffixA !== "R" && suffixB === "R") {
    return 1;
  } else {
    return 0;
  }
};

export const sortImageUrls = (image_urls) => {
  return image_urls.sort((a, b) => {
    if (a.includes("-L")) return -1;
    if (b.includes("-L")) return 1;
    return 0;
  });
};

export function formatOutletData(initialData, t = (s) => s, language) {
  if (!initialData) return {};
  const labels = Object.keys(initialData)
    .map((key) => {
      if (initialData[key] <= 0) return null;
      switch (key) {
        case "energyRecovery":
          return t("Valorisation\nénergétique");
        case "materialRecovery":
          return t("Valorisation\nmatière");
        case "landfill":
          return t("Stockage");
        case "undefined":
          return t("Non défini");
        default:
          return null;
      }
    })
    .filter((label) => label !== null);
  const data = Object.values(initialData)
    .map((value) => (value > 0 ? value / 1000 : null))
    .filter((v) => v !== null);
  const sum = data.reduce((acc, v) => acc + v, 0);
  return { labels, data, sum };
}

export const aggregateMaterialDelivery = (deliveries) => {
  const aggregated = new Map();

  deliveries.forEach((item) => {
    const key = item.cls;
    const existing = aggregated.get(key);

    if (!existing) {
      aggregated.set(key, {
        cls: item.cls,
        referenceName: item.referenceName ?? item.cls,
        count: item.count || 0,
        volumeObject: item.volumeObject || 0,
        massObject: item.massObject || 0,
        finesMass: item.finesMass || 0,
        surface: item.surface || 0,
        parentCategory: item.parentCategory,
        recoverable: item.recoverable,
        rep: item.rep,
        isWeight: item.isWeight,
        density: item.density,
        finesProportion: item.finesProportion,
      });
    } else {
      existing.count += item.count || 0;
      existing.volumeObject += item.volumeObject || 0;
      existing.massObject += item.massObject || 0;
      existing.finesMass += item.finesMass || 0;
      existing.surface += item.surface || 0;
    }
  });

  return Array.from(aggregated.values());
};

/**
 * Sort REP channel keys like the on-screen EPR table:
 * alphabetical by translated label, "Without EPR" (Sans REP) last.
 */
export const sortRepAssociatedKeys = (keys, translate) => {
  const tFn = typeof translate === "function" ? translate : (k) => k;
  return [...keys].sort((a, b) => {
    const aIsSansRep = a === "Without EPR";
    const bIsSansRep = b === "Without EPR";
    if (aIsSansRep && !bIsSansRep) return 1;
    if (!aIsSansRep && bIsSansRep) return -1;
    if (aIsSansRep && bIsSansRep) return 0;
    return tFn(a).localeCompare(tFn(b));
  });
};

/**
 * Format date as DD/MM/YYYY with localized time separator.
 * Example (fr): 07/11/2025 à 14h46
 * Example (en): 07/11/2025 at 14:46
 */
export const formatCharacterizationDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const language = i18n.language || "fr";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const separator =
    language === "fr"
      ? "à"
      : language === "es"
        ? "a las"
        : language === "se"
          ? "kl"
          : "at";
  const timePart = language.startsWith("fr")
    ? `${hours}h${minutes}`
    : `${hours}:${minutes}`;
  return `${day}/${month}/${year} ${separator} ${timePart}`;
};
